import React, { useState } from 'react';
import { Upload, Image, Popover, message, Spin } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ossApi } from '@/services/oss';
import { useAuthStore } from '@/store/auth';
import axios from 'axios';

interface ImageUploadProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  accept?: string;
  maxSize?: number; // 最大文件大小，单位MB
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  className,
  style,
  disabled = false,
  accept = 'image/*',
  maxSize = 5, // 默认最大5MB
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>(value ? [{
    uid: '-1',
    name: 'current-image',
    status: 'done',
    url: value,
  }] : []);
  const [loading, setLoading] = useState(false);
  const { token } = useAuthStore();

  // 上传文件到OSS
  const uploadToOSS = async (file: File): Promise<any> => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return false;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      message.error(`图片大小不能超过 ${maxSize}MB!`);
      return false;
    }
    const formData = new FormData();
    formData.append('file', file);

    try {
      // 使用 axios 直接发起上传请求，因为我们需要添加认证头
      const response = await axios.post(ossApi.uploadPublicUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      // 检查响应是否符合预期格式
      if (response.data && typeof response.data === 'object') {
        // 如果是统一错误码格式
        if ('error_code' in response.data) {
          if (response.data.error_code !== 0) {
            throw new Error(response.data.message || '上传失败');
          }
          // 成功响应，解包 data 字段
          return response.data.data.preview_url || response.data.data.file_path; // 优先使用 preview_url
        } else {
          // 直接返回数据
          return response.data.preview_url || response.data.file_path; // 优先使用 preview_url
        }
      }

      throw new Error('上传响应格式错误');
    } catch (error: any) {
      console.error('上传图片失败:', error);
      throw new Error(`图片上传失败: ${error.message || '网络错误'}`);
    }
  };

  const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    setFileList(newFileList);

    // 查找待上传的文件（有 originFileObj 但还没有上传过的文件）
    const pendingFile = newFileList.find(file =>
      file.originFileObj &&
      !file.url?.startsWith('http') &&
      !file.url?.startsWith('blob:') &&
      file.status !== 'uploading'
    );

    // 如果有待上传的文件，执行上传
    if (pendingFile) {
      setLoading(true);
      try {
        const uploadedUrl = await uploadToOSS(pendingFile.originFileObj as File);
        onChange?.(uploadedUrl);
        message.success('图片上传成功！');

        // 更新文件列表，将文件状态改为 done 并设置正确的 URL
        const updatedFileList = newFileList.map(file => {
          if (file.uid === pendingFile.uid) {
            return {
              ...file,
              status: 'done',
              url: uploadedUrl,
            } as UploadFile;
          }
          return file;
        });
        setFileList(updatedFileList);
      } catch (error: any) {
        message.error(error.message || '图片上传失败');
        // 移除上传失败的文件
        const updatedFileList = newFileList.filter(f => f.uid !== pendingFile.uid);
        setFileList(updatedFileList);
      } finally {
        setLoading(false);
      }
    } else {
      // 处理其他情况，比如已有文件的回显
      const file = newFileList[newFileList.length - 1];
      if (file && file.url && file.url.startsWith('http') && file.status === 'done') {
        // 如果是已存在的文件（如编辑时回显）
        onChange?.(file.url);
      }
    }
  };

  // 渲染缩略图
  const thumbnail = (
    <div
      className={`inline-block w-20 h-20 rounded cursor-pointer border border-gray-200 overflow-hidden flex items-center justify-center bg-white ${className}`}
      style={style}
    >
      {loading ? (
        <Spin size="small" />
      ) : value ? (
        <img
          src={value}
          alt="thumbnail"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex flex-col items-center">
          <UploadOutlined className="text-lg" />
          <span className="text-xs mt-1">上传</span>
        </div>
      )}
    </div>
  );

  return (
    <div className='inline-block'>
      <Spin spinning={loading}>
        <Popover
          content={
            value ? (
              <Image
                src={value}
                width={300}
                height={300}
                style={{ objectFit: 'contain', maxWidth: '100%', maxHeight: '300px' }}
                fallback="/images/default-icon.png"
              />
            ) : (
              <div>暂无图片预览</div>
            )
          }
          trigger="hover"
          placement="right"
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            //beforeUpload={handleBeforeUpload}
            beforeUpload={() => false}
            onChange={handleChange}
            fileList={fileList}
            disabled={disabled || loading}
            accept={accept}
            className="!p-0"
          >
            {thumbnail}
          </Upload>
        </Popover>
      </Spin>
    </div>
  );
};

export default ImageUpload;
